/**
 * SBU HERO CAROUSEL — the behaviour of sections/sbu-hero-carousel.liquid.
 *
 * Everything here is enhancement: the markup swipes by scroll-snap, the dots
 * are anchors, and the arrows and pause button are not painted until this
 * element is defined (`sbu-hero-carousel:not(:defined)` in the section).
 *
 * THE CONTRACT the section meets: `data-sbu-hero-track` on the scroller, each
 * slide a direct child one viewport wide; `data-sbu-autoplay="<ms>"` on this
 * element (0 or absent for off); dots as anchors with `data-sbu-hero-dot=
 * "<index>"`; arrows with `data-sbu-hero-arrow="prev|next"`; a pause button
 * with `data-sbu-hero-pause`, both `data-sbu-label-*` names and two icons
 * tagged `data-sbu-hero-icon`; a status line with `data-sbu-hero-status` (the
 * live region, outside the track) carrying `data-sbu-hero-status-template`
 * with `[i]` and `[n]` for the slide number and the count.
 *
 * THE WRAP (plan §C3). After the last slide comes the first, and under
 * `scroll-snap-type: x mandatory` a smooth scroll back is a sweep across every
 * slide — the backward motion the operator struck for the rail on 2026-09-09.
 * So a wrap is an INSTANT jump masked by a cross-fade: the track fades to the
 * frame's indigo (`.sbu-hero__track--wrapping`, duration read from the CSS),
 * scrollLeft is written, the track fades back. Not the rail's rotate-and-
 * subtract: moving slide nodes would desync the dots, "N of M" and aria-current.
 *
 * HOLDS, after assets/sbu-shelf-rail.js: one set of reasons, not one flag —
 * hover, press, keyboard focus (a click focuses a button too and must not stop
 * the strip for good), off screen (IntersectionObserver 0.2), a hidden
 * document, two intervals after a touch swipe. Pause stops it for the page
 * view (WCAG 2.2.2). Never starts under prefers-reduced-motion (2.3.3).
 */
class SbuHeroCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */ #abortController;
  /** @type {IntersectionObserver | undefined} */ #intersectionObserver;
  /** @type {number} setInterval id, 0 at rest */ #timer = 0;
  /** @type {number} setTimeout id of the post-swipe hold, 0 when none */ #swipeHold = 0;
  /** @type {number} requestAnimationFrame id of the pending scroll read */ #frame = 0;
  /** @type {Set<'hover' | 'press' | 'focus'>} */ #holds = new Set();
  /** @type {boolean} */ #onScreen = true;
  /** @type {boolean} the visitor pressed Pause; lasts the page view */ #paused = false;
  /** @type {boolean} a wrap's fade is running */ #wrapping = false;
  /** @type {boolean} a finger is down on the track */ #fingerDown = false;
  /** @type {boolean} the track scrolled while a finger was down */ #swiped = false;
  /** @type {number} the slide at the track's start edge */ #index = 0;

  connectedCallback() {
    // A fresh controller on every connect: the editor and the Section
    // Rendering API detach and re-attach, and an aborted controller is spent.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const track = this.track;
    if (!track) return;
    this.#index = this.#nearest();

    for (const arrow of this.querySelectorAll('[data-sbu-hero-arrow]')) {
      const direction = arrow.getAttribute('data-sbu-hero-arrow') === 'next' ? 1 : -1;
      arrow.addEventListener('click', () => this.#step(direction), { signal });
    }
    for (const dot of this.dots) {
      // Intercepted: a fragment jump scrolls the page too (plan §C3), and
      // the hash is left alone.
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.#goTo(Number(dot.getAttribute('data-sbu-hero-dot')));
      }, { signal });
    }
    this.pauseButton?.addEventListener('click', () => this.#togglePause(), { signal });

    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });

    this.addEventListener('pointerenter', () => this.#hold('hover'), { signal });
    this.addEventListener('pointerleave', () => this.#release('hover', 'press'), { signal });
    this.addEventListener('pointerdown', () => this.#hold('press'), { signal });
    this.addEventListener('pointerup', () => this.#release('press'), { signal });
    this.addEventListener('pointercancel', () => this.#release('press'), { signal });

    // A swipe earns two quiet intervals: the visitor chose a slide.
    track.addEventListener('touchstart', () => { this.#fingerDown = true; this.#swiped = false; }, { passive: true, signal });
    const lifted = (event) => {
      if (/** @type {TouchEvent} */ (event).touches.length > 0) return;
      this.#fingerDown = false;
      if (this.#swiped) this.#holdAfterSwipe();
    };
    track.addEventListener('touchend', lifted, { passive: true, signal });
    track.addEventListener('touchcancel', lifted, { passive: true, signal });

    this.addEventListener('focusin', (event) => {
      const el = event.target;
      if (el instanceof Element && el.matches(':focus-visible')) this.#hold('focus');
    }, { signal });
    this.addEventListener('keydown', () => {
      if (this.contains(document.activeElement)) this.#hold('focus');
    }, { signal });
    this.addEventListener('focusout', (event) => {
      const next = /** @type {FocusEvent} */ (event).relatedTarget;
      if (next instanceof Node && this.contains(next)) return;
      this.#release('focus');
    }, { signal });

    document.addEventListener('visibilitychange', () => this.#retime(), { signal });
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver((entries) => {
        this.#onScreen = entries.some((entry) => entry.isIntersecting);
        this.#retime();
      }, { threshold: 0.2 });
      this.#intersectionObserver.observe(track);
    }
    // A resize re-snaps the browser's way; put the strip back on its slide.
    window.addEventListener('resize', () => this.#goTo(this.#index, 'auto'), { signal });

    const pause = this.pauseButton;
    if (pause && this.#reducedMotion) {
      // Nothing moves on its own, so there is nothing to pause.
      pause.hidden = true;
      this.#live('polite');
    }

    this.#reflect();
    this.#retime();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#stop();
    clearTimeout(this.#swipeHold);
    this.#swipeHold = 0;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** @returns {HTMLElement | null} */
  get track() { return this.querySelector('[data-sbu-hero-track]'); }
  /** @returns {HTMLAnchorElement[]} */
  get dots() { return Array.from(this.querySelectorAll('[data-sbu-hero-dot]')); }
  /** @returns {HTMLButtonElement | null} */
  get pauseButton() { return this.querySelector('[data-sbu-hero-pause]'); }
  /** @returns {HTMLElement | null} the live region */
  get status() { return this.querySelector('[data-sbu-hero-status]'); }
  /** @returns {number} slides in the track */
  get #count() { return this.track?.childElementCount ?? 0; }
  /** Milliseconds between advances; 0 means never. @returns {number} */
  get #interval() { return Number(this.getAttribute('data-sbu-autoplay') || 0); }
  get #reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  /** The slide whose start edge is nearest the track's. @returns {number} */
  #nearest() {
    const track = this.track;
    if (!track || track.clientWidth <= 0) return 0;
    return Math.max(0, Math.min(this.#count - 1, Math.round(track.scrollLeft / track.clientWidth)));
  }

  /** One read per frame: which slide shows; the dots follow. */
  #onScroll = () => {
    if (this.#fingerDown) this.#swiped = true;
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const index = this.#nearest();
      if (index === this.#index) return;
      this.#index = index;
      this.#reflect();
    });
  };

  /**
   * aria-current on the dot of the slide at the start edge, and "Slide N of M"
   * into the status line — the one DOM change the live region can announce,
   * since the advance itself is a scroll.
   */
  #reflect() {
    for (const dot of this.dots) {
      if (Number(dot.getAttribute('data-sbu-hero-dot')) === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
    const status = this.status;
    const template = status?.getAttribute('data-sbu-hero-status-template');
    if (status && template) {
      status.textContent = template.replace('[i]', String(this.#index + 1)).replace('[n]', String(this.#count));
    }
  }

  /**
   * The live region's politeness: off while the strip rotates, polite once the
   * visitor stopped it or never had motion (APG carousel).
   * @param {'off' | 'polite'} value
   */
  #live(value) {
    (this.status ?? this.track)?.setAttribute('aria-live', value);
  }

  /**
   * Scroll the TRACK to slide `index` — smooth unless told otherwise or the
   * visitor asked for less motion; never the page.
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #goTo(index, behavior) {
    const track = this.track;
    if (!track || this.#count === 0) return;
    const target = Math.max(0, Math.min(this.#count - 1, index));
    track.scrollTo({ left: target * track.clientWidth, behavior: behavior ?? (this.#reducedMotion ? 'auto' : 'smooth') });
  }

  /**
   * One slide forward or back, wrapping at either end. A wrap under motion is
   * the masked jump (header); under reduced motion every move is instant.
   * @param {1 | -1} direction
   */
  #step(direction) {
    const n = this.#count;
    if (n < 2 || this.#wrapping) return;
    const next = this.#index + direction;
    if (next >= 0 && next < n) return this.#goTo(next);
    const wrapped = next < 0 ? n - 1 : 0;
    if (this.#reducedMotion) return this.#goTo(wrapped, 'auto');

    const track = this.track;
    if (!track) return;
    const fade = parseFloat(getComputedStyle(track).transitionDuration) * 1000 || 0;
    this.#wrapping = true;
    track.classList.add('sbu-hero__track--wrapping');
    window.setTimeout(() => {
      this.#goTo(wrapped, 'auto');
      track.classList.remove('sbu-hero__track--wrapping');
      this.#wrapping = false;
    }, fade);
  }

  // ---- AUTOPLAY -----------------------------------------------------------

  #advance = () => { if (!this.#wrapping) this.#step(1); };

  /** @param {'hover' | 'press' | 'focus'} reason */
  #hold(reason) { this.#holds.add(reason); this.#stop(); }

  /** Retimes only when the set empties. @param {...('hover' | 'press' | 'focus')} reasons */
  #release(...reasons) {
    let removed = false;
    for (const reason of reasons) removed = this.#holds.delete(reason) || removed;
    if (removed && this.#holds.size === 0) this.#retime();
  }

  #holdAfterSwipe() {
    this.#stop();
    clearTimeout(this.#swipeHold);
    this.#swipeHold = window.setTimeout(() => {
      this.#swipeHold = 0;
      this.#retime();
    }, 2 * this.#interval);
  }

  #stop() { clearInterval(this.#timer); this.#timer = 0; }

  /** Start or stop the timer against every gate. */
  #retime() {
    this.#stop();
    if (this.#interval <= 0 || this.#count < 2) return;
    if (this.#paused || this.#holds.size > 0 || this.#swipeHold) return;
    if (!this.#onScreen || document.hidden) return;
    if (this.#reducedMotion) return;
    this.#timer = window.setInterval(this.#advance, this.#interval);
  }

  /** Pause/Play: aria-pressed, the name, the icon, and the live region. */
  #togglePause() {
    const button = this.pauseButton;
    if (!button) return;
    this.#paused = !this.#paused;
    button.setAttribute('aria-pressed', String(this.#paused));
    const label = button.getAttribute(this.#paused ? 'data-sbu-label-play' : 'data-sbu-label-pause');
    if (label) button.setAttribute('aria-label', label);
    for (const icon of button.querySelectorAll('[data-sbu-hero-icon]')) {
      icon.toggleAttribute('hidden', (icon.getAttribute('data-sbu-hero-icon') === 'pause') === this.#paused);
    }
    // APG carousel: announce changes only once the visitor stopped the rotation.
    this.#live(this.#paused ? 'polite' : 'off');
    this.#retime();
  }
}

// Loaded by URL from the section, so the browser de-duplicates the module;
// the guard covers a second definition after a morph. Define once.
if (!customElements.get('sbu-hero-carousel')) {
  customElements.define('sbu-hero-carousel', SbuHeroCarousel);
}
